import React, { useState } from 'react';
import BallsDisplay from '../../../LottoAssistant/components/BallsDisplay';
import BetStatusBadge from '../../../LottoAssistant/components/BetStatusBadge';
import { renderLotteryLogo } from '../../../LottoAssistant/utils/renderLotteryLogo';
import { formatLottoDate } from '../../../LottoAssistant/utils/date';
import {
  isBallBasedMarket,
  formatOdds,
} from '../../../LottoAssistant/utils/common';
import { formatCategory } from '../../../LottoAssistant/utils/marketTypeHelpers';
import { isWon, formatMoney } from '../../utils';
import classes from './BetCard.module.scss';

const TrashIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 64 64"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M28 7c-2.757 0-5 2.243-5 5v3H13a2 2 0 0 0 0 4h2.11l1.683 30.332A6.003 6.003 0 0 0 22.783 55h18.434a6.004 6.004 0 0 0 5.99-5.668L48.891 19H51a2 2 0 0 0 0-4H41v-3c0-2.757-2.243-5-5-5h-8zm0 4h8c.552 0 1 .449 1 1v3H27v-3c0-.551.448-1 1-1zm-8.887 8h25.774l-1.674 30.11A2 2 0 0 1 41.217 51H22.783a2 2 0 0 1-1.996-1.889L19.113 19zM32 23.25A1.75 1.75 0 0 0 30.25 25v20a1.75 1.75 0 1 0 3.5 0V25A1.75 1.75 0 0 0 32 23.25zm-7.357.002a1.75 1.75 0 0 0-1.688 1.809l.697 20.085a1.75 1.75 0 0 0 1.748 1.69h.063a1.751 1.751 0 0 0 1.687-1.81l-.697-20.087a1.76 1.76 0 0 0-1.81-1.687zm14.712 0a1.753 1.753 0 0 0-1.808 1.687l-.697 20.086a1.75 1.75 0 0 0 1.687 1.81c.021.002.042 0 .063 0a1.75 1.75 0 0 0 1.748-1.689l.697-20.085a1.75 1.75 0 0 0-1.69-1.809z" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="7" height="13" viewBox="0 0 7 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="currentColor"
      d="M0.970131 12.5C1.23775 12.5 1.46356 12.4079 1.64755 12.2238L6.65711 7.26151C6.89128 7.03556 7 6.79289 7 6.5C7 6.20711 6.89128 5.95607 6.66547 5.73849L1.64755 0.776151C1.46356 0.59205 1.23775 0.5 0.970131 0.5C0.434886 0.5 0 0.926778 0 1.46234C0 1.73013 0.108722 1.9728 0.301075 2.17364L4.70012 6.50837L0.301075 10.8347C0.108722 11.0272 0 11.2699 0 11.5377C0 12.0732 0.434886 12.5 0.970131 12.5Z"
    />
  </svg>
);

const BetCard = ({ bet, variant, onCancel, isCancelling, cancelError }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSettled = variant === 'settled';
  const isBallBased = isBallBasedMarket(bet.outcome?.outcome_type);
  const hasMultipleWagers = (bet.wagers?.length ?? 0) > 1;

  const headerActions = isSettled ? (
    <BetStatusBadge variant={bet.status} />
  ) : (
    onCancel && (
      <button
        type="button"
        className={classes.IconButton}
        disabled={isCancelling}
        onClick={(e) => {
          e.stopPropagation();
          onCancel(bet.wager_set_ref);
        }}
        aria-label="Cancel bet"
      >
        <TrashIcon />
      </button>
    )
  );

  const marketLine = (
    <p className={classes.MetaText}>
      {isBallBased
        ? `${formatCategory(bet.market_category)} · ${bet.market_title}`
        : `${bet.market_title} · ${bet.outcome?.outcome_title ?? ''}`}
    </p>
  );

  return (
    <div className={classes.Card}>
      {hasMultipleWagers ? (
        <div
          className={classes.ClickableHeader}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <div className={classes.Logo}>{renderLotteryLogo(bet.draw_id)}</div>
          <div className={classes.HeaderInfo}>
            <div className={classes.HeaderInfoTop}>
              <p className={classes.LotteryName}>{bet.lottery_name}</p>
            </div>
            <p className={classes.MetaText}>
              {`Number of bets: ${bet.wagers.length}`}
            </p>
          </div>

          <div className={classes.ActionsContainer}>
            {headerActions}
            <button
              type="button"
              className={
                classes.ArrowButton + ' ' +
                (isExpanded ? classes.ArrowExpanded : classes.ArrowCollapsed)
              }
              aria-label="Toggle"
            >
              <ArrowIcon />
            </button>
          </div>
        </div>
      ) : (
        <div className={classes.Header}>
          <div className={classes.HeaderRow}>
            <div className={classes.Logo}>{renderLotteryLogo(bet.draw_id)}</div>
            <p className={classes.LotteryName}>{bet.lottery_name}</p>
            <div className={classes.ActionsContainer}>{headerActions}</div>
          </div>
        </div>
      )}

      <div className={classes.DatesRow}>
        <div>
          <p className={classes.MetaText + ' ' + classes.MetaTextSpaced}>Created:</p>
          <p className={classes.MetaValue}>
            {formatLottoDate(bet.inserted_at).replace(' ', ' · ')}
          </p>
        </div>

        {bet.draw_date && (
          <div>
            <p className={classes.MetaText + ' ' + classes.MetaTextSpaced}>Draw date:</p>
            <p className={classes.MetaValue}>
              {formatLottoDate(bet.draw_date).replace(' ', ' · ')}
            </p>
          </div>
        )}
      </div>

      <div className={classes.MarketLine}>{marketLine}</div>

      {!hasMultipleWagers && <div className={classes.Divider} />}

      {bet.balls?.length > 0 && (
        <>
          {hasMultipleWagers && <div className={classes.Divider} />}
          <div className={classes.BallsRow}>
            <BallsDisplay numbers={bet.balls} size="medium" />
          </div>
          <div className={classes.Divider} />
        </>
      )}

      {hasMultipleWagers && (
        <div className={classes.Collapse + ' ' + (isExpanded ? classes.Open : '')}>
          {bet.wagers.map((wager, i) => (
            <div key={wager.wager_ref ?? i} className={classes.WagerRow}>
              <p className={classes.WagerIndex}>{i + 1}</p>
              <div className={classes.WagerBalls}>
                <BallsDisplay numbers={wager.balls} size="small" />
              </div>
              <BetStatusBadge variant={wager.status} />
            </div>
          ))}
          {cancelError && (
            <p className={classes.ErrorText} style={{ padding: '8px 20px' }}>
              {cancelError}
            </p>
          )}
        </div>
      )}

      <div className={classes.StakeOddsSection}>
        <div className={classes.StakeOddsCol}>
          <p className={classes.FooterLabel}>STAKE</p>
          <p className={classes.FooterValue}>
            {formatMoney(bet.set_amount, 'ZAR')}
          </p>
        </div>
        <div className={classes.StakeOddsCol}>
          <p className={classes.FooterLabel}>ODDS</p>
          <p className={classes.FooterValue}>
            {formatOdds(bet.outcome?.odds)}
          </p>
        </div>
      </div>

      <div className={classes.PayoutSection}>
        {isSettled ? (
          <>
            <p className={classes.PayoutLabel}>
              {isWon(bet.status) ? 'PAYOUT' : ''}
            </p>
            <p
              className={
                classes.PayoutValueSettled +
                (isWon(bet.status) ? ' ' + classes.PayoutValueSettledWon : '')
              }
            >
              {formatMoney(bet.final_payout ?? 0, 'ZAR')}
            </p>
          </>
        ) : (
          <>
            <p className={classes.PayoutLabel}>POTENTIAL PAYOUT</p>
            <p className={classes.PayoutValue}>
              {formatMoney(bet.potential_payout, 'ZAR')}
            </p>
          </>
        )}
      </div>

      {!hasMultipleWagers && cancelError && (
        <div className={classes.CancelBtnWrapper}>
          <p className={classes.ErrorText}>{cancelError}</p>
        </div>
      )}
    </div>
  );
};

export default BetCard;
